import React, { useMemo, useState } from "react";
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from "react-native";
import { useTheme } from "../../../../../theme/useTheme";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import Svg, {
  Circle,
  Line as SvgLine,
  Polyline,
  Rect,
  Text as SvgText,
} from "react-native-svg";
import { FontFamily } from "../../../../../constants/fonts";
import DLOTooltip from "../../../../../modules/dlo/components/DLOTooltip";
import { useToast } from "../../../../../components/LhToastContainer";
import { formatCount } from "../utils/formatUtils";
import type { KpiExecutiveOverviewResponse } from "../services/onTimeDispatchKpiApi";
import { useCompletedTrend } from "../hooks/useCompletedTrend";
import { useOnTimeTrend } from "../hooks/useOnTimeTrend";
import { useDriverArrivedTrend } from "../hooks/useDriverArrivedTrend";
import { useCompletedVariance } from "../hooks/useCompletedVariance";
import { useOnTimeVariance } from "../hooks/useOnTimeVariance";
import { useDriverArrivalVariance } from "../hooks/useDriverArrivalVariance";
import type { TrendPoint as ApiTrendPoint } from "../services/completedTrendApi";
import {
  stableHash,
  deterministicBetween,
  normalizeFilterValue,
  getRegionLabel,
  getMarketLabel,
  getLocationLabel,
} from "../OnTimeDispatchDashboard";
import type { AppliedFiltersSnapshot } from "../OnTimeDispatchDashboard";

type ChartHierarchyLevel = "region" | "market" | "location";
type ChartTimeframeGrain = "year" | "period" | "week" | "day";
type ManualLocationHierarchy = ChartHierarchyLevel | null;
type ManualTimeframe = ChartTimeframeGrain | null;

const CHART_HIERARCHY_OPTIONS: { key: ChartHierarchyLevel; label: string }[] = [
  { key: "region", label: "Region / Unit" },
  { key: "market", label: "Market / POD" },
  { key: "location", label: "Location" },
];

const CHART_TIMEFRAME_OPTIONS: { key: ChartTimeframeGrain; label: string }[] = [
  { key: "year", label: "Year" },
  { key: "period", label: "Period" },
  { key: "week", label: "Week" },
  { key: "day", label: "Day" },
];

const KPI_TARGETS = {
  targetCompletedPct: 98.5,
  targetOnTimePct: 98.5,
  targetDriverArrivedPct: 85.0,
};

// Sized so ScrollView + axis row together equal the trend card's fixed 230px plot height.
const VARIANCE_AXIS_ROW_HEIGHT = 22;
const VARIANCE_AXIS_ROW_MARGIN_TOP = 4;
const DEFAULT_VARIANCE_LIST_HEIGHT =
  230 - VARIANCE_AXIS_ROW_HEIGHT - VARIANCE_AXIS_ROW_MARGIN_TOP;

interface KpiInfoCardProps {
  title: string;
  value: string;
  subText: string;
  tooltip: string;
  hideTooltipTitle?: boolean;
  valueTone?: "positive" | "negative" | "neutral";
  onPress?: () => void;
  /** Phone dark mode styling — scoped to caller */
  isDark?: boolean;
  /** Applies the selected-card appearance (used for the Incomplete Loads
   * KPI card when the applied Status filter contains "Incomplete"). */
  isSelected?: boolean;
}

interface TrendPoint {
  label: string;
  value: number;
  secondaryLabel?: string;
}

interface VariancePoint {
  label: string;
  variance: number;
}

interface LineTrendCardProps {
  title: string;
  titleQualifier?: string;
  subtitle?: string;
  xLabels: string[];
  xSecondaryLabels?: string[];
  yMin: number;
  yMax: number;
  points: number[];
  lineColor: string;
  axisSuffix?: string;
  yTicks?: number[];
  targetLineValue?: number;
  targetLineColor?: string;
  showPointLabels?: boolean;
  pointLabelPrecision?: number;
  plotLeftPad?: number;
  plotRightPad?: number;
  plotTopPad?: number;
  plotBottomPad?: number;
  plotWidth?: number;
  plotHeight?: number;
  plotTextFontSize?: number;
  pointFillColor?: string;
  pointStrokeColor?: string;
  pointStrokeWidth?: number;
  chartType?: "line" | "bar";
  /** Phone dark mode styling — scoped to caller */
  isDark?: boolean;
}

interface VarianceChartCardProps {
  title: string;
  titleQualifier?: string;
  subtitle?: string;
  axisLeftLabel: string;
  axisCenterLabel: string;
  axisRightLabel: string;
  maxAbsVariance: number;
  bars: VariancePoint[];
  positiveColor: string;
  valuePrecision?: number;
  maxListHeight?: number;
  /** Phone dark mode styling — scoped to caller */
  isDark?: boolean;
}

const fmtPct = (numerator: number, denominator: number): string => {
  if (!denominator) return "0.00%";
  return `${((numerator / denominator) * 100).toFixed(2)}%`;
};

const TREND_AXIS_PADDING = 5;
const HIGH_VALUE_TREND_AXIS_PADDING = 3;

const getDynamicTrendAxisRange = (
  points: TrendPoint[],
  targetValue: number | null | undefined,
  fallbackMin: number,
  fallbackMax: number
): { min: number; max: number } => {
  const values = points
    .map((point) => point.value)
    .filter((value) => Number.isFinite(value));

  if (typeof targetValue === "number" && Number.isFinite(targetValue)) {
    values.push(targetValue);
  }

  if (values.length === 0) {
    return { min: fallbackMin, max: fallbackMax };
  }

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const snappedMin = Math.floor(dataMin);
  const snappedMax = Math.ceil(dataMax);
  const axisPadding = snappedMin >= 99 ? HIGH_VALUE_TREND_AXIS_PADDING : TREND_AXIS_PADDING;
  const min = snappedMin - axisPadding;
  const max = snappedMax + axisPadding;

  if (min === max) {
    return {
      min: min - axisPadding,
      max: max + axisPadding,
    };
  }

  return { min, max };
};

const getIntegerTrendTicks = (min: number, max: number, targetTickCount = 5): number[] => {
  const start = Math.floor(min);
  const end = Math.ceil(max);

  if (start >= end) {
    return [start];
  }

  const span = end - start;
  const step = Math.max(1, Math.ceil(span / Math.max(targetTickCount - 1, 1)));
  const ticks: number[] = [];

  for (let tick = start; tick <= end; tick += step) {
    ticks.push(tick);
  }

  if (ticks[ticks.length - 1] !== end) {
    ticks.push(end);
  }

  return ticks;
};

const formatTrendAxisTick = (tick: number): string => {
  return Math.round(tick).toFixed(0);
};

const generateTrendSeries = (
  labels: string[],
  min: number,
  max: number,
  keyPrefix: string
): TrendPoint[] => {
  const span = max - min;
  return labels.map((label, index) => {
    const raw = deterministicBetween(
      `${keyPrefix}-${label}`,
      min + span * 0.15,
      max - span * 0.08
    );
    const seasonal =
      Math.sin((index / Math.max(labels.length - 1, 1)) * Math.PI * 1.2) *
      (span * 0.06);
    const value = Math.max(min, Math.min(max, raw + seasonal));
    return { label, value: Number(value.toFixed(2)) };
  });
};

// Renders the secondary x-axis line for API-backed trend charts (e.g. "P1" above, "'25" below).
const trendSecondaryLabel = (point: ApiTrendPoint): string | undefined => {
  if (point.year != null) return `'${String(point.year).slice(-2)}`;
  if (point.period != null) return `P${point.period}`;
  return undefined;
};

const generateVarianceSeries = (
  labels: string[],
  maxAbsVariance: number,
  keyPrefix: string
): VariancePoint[] => {
  return labels.map((label) => {
    const magnitude = deterministicBetween(
      `${keyPrefix}-${label}-mag`,
      0.15,
      maxAbsVariance
    );
    const signSeed = stableHash(`${keyPrefix}-${label}-sign`) % 10;
    const sign = signSeed < 6 ? 1 : -1;
    const variance = Number((magnitude * sign).toFixed(2));
    return { label, variance };
  });
};

// Rule-based auto drill-down: precedence follows the most specific applied filter
const inferLocationHierarchy = (appliedFilters: AppliedFiltersSnapshot): ChartHierarchyLevel => {
  if (appliedFilters.selectedLocations.length > 0) {
    return "location";
  }
  if (appliedFilters.selectedMarkets.length === 1) {
    return "market";
  }
  if (appliedFilters.selectedRegions.length === 1) {
    return "market";
  }
  return "region";
};

const inferTimeframe = (appliedFilters: AppliedFiltersSnapshot): ChartTimeframeGrain => {
  if (appliedFilters.selectedWeeks.length > 0) {
    return "day";
  }
  if (appliedFilters.selectedPeriods.length === 1) {
    return "week";
  }
  if (appliedFilters.selectedYears.length === 1) {
    return "period";
  }
  return "period";
};

const LineTrendCard: React.FC<LineTrendCardProps> = ({
  title,
  titleQualifier,
  subtitle,
  xLabels,
  xSecondaryLabels,
  yMin,
  yMax,
  points,
  lineColor,
  axisSuffix = "%",
  yTicks,
  targetLineValue,
  targetLineColor = "#69A7E8",
  showPointLabels = false,
  pointLabelPrecision = 1,
  plotLeftPad = 34,
  plotRightPad = 34,
  plotTopPad = 20,
  plotBottomPad = 34,
  plotWidth = 540,
  plotHeight = 220,
  plotTextFontSize = 11,
  pointFillColor,
  pointStrokeColor,
  pointStrokeWidth = 1,
  chartType = "line",
  isDark = false,
}) => {
  const chartHeight = plotHeight;
  const leftPad = plotLeftPad;
  const rightPad = plotRightPad;
  const topPad = plotTopPad;
  const hasSecondaryXAxisLabels = Boolean(
    xSecondaryLabels?.some((label) => Boolean(label))
  );
  const bottomPad = Math.max(
    plotBottomPad,
    hasSecondaryXAxisLabels ? 44 : 30
  );

  // Avoid squeezing/overlapping x-axis labels and points when there are many data points
  // (e.g. daily grain): widen the plot to a minimum per-point spacing and scroll horizontally
  // instead of compressing everything into the fixed card width.
  const MIN_POINT_SPACING = 46;
  const baseInnerWidth = plotWidth - leftPad - rightPad;
  const innerWidth = Math.max(baseInnerWidth, (points.length - 1) * MIN_POINT_SPACING);
  const needsHorizontalScroll = innerWidth > baseInnerWidth;
  const chartWidth = leftPad + rightPad + innerWidth;
  const innerHeight = chartHeight - topPad - bottomPad;
  const minTickY = topPad + innerHeight;
  const xAxisY = minTickY + 8;
  const xAxisPrimaryLabelY = xAxisY + 14;
  const xAxisSecondaryLabelY = xAxisPrimaryLabelY + 14;

  const resolvedYTicks = useMemo(() => {
    if (yTicks?.length) {
      return [...yTicks]
        .map((tick) => Math.round(tick))
        .sort((a, b) => a - b)
        .filter((tick, index, ticks) => index === 0 || tick !== ticks[index - 1]);
    }
    return getIntegerTrendTicks(yMin, yMax);
  }, [yMin, yMax, yTicks]);

  const targetY = useMemo(() => {
    if (typeof targetLineValue !== "number") return null;
    return (
      topPad + ((yMax - targetLineValue) / (yMax - yMin || 1)) * innerHeight
    );
  }, [innerHeight, targetLineValue, topPad, yMax, yMin]);

  const polylinePoints = useMemo(() => {
    return points.map((value, index) => {
      const x = leftPad + (index / Math.max(points.length - 1, 1)) * innerWidth;
      const y = topPad + ((yMax - value) / (yMax - yMin || 1)) * innerHeight;
      return { x, y, value };
    });
  }, [points, innerWidth, innerHeight, leftPad, topPad, yMax, yMin]);

  const svgGridLineColor = isDark ? '#363636' : '#E5E7EB';
  const svgAxisLabelColor = isDark ? '#B0B0B0' : '#6B7280';
  const svgAxisLineColor = isDark ? '#404040' : '#CBD5E1';

  const svgChildren = (
    <>
      {resolvedYTicks.map((tick) => {
        const y = topPad + ((yMax - tick) / (yMax - yMin || 1)) * innerHeight;
        return (
          <React.Fragment key={`tick-${tick}`}>
            <SvgLine
              x1={leftPad}
              y1={y}
              x2={chartWidth - rightPad}
              y2={y}
              stroke={svgGridLineColor}
              strokeWidth="1"
            />
            <SvgText
              x={leftPad - 8}
              y={y + 4}
              fill={svgAxisLabelColor}
              fontSize={plotTextFontSize}
              fontFamily={FontFamily.bold}
              fontWeight="500"
              textAnchor="end"
            >
              {`${formatTrendAxisTick(tick)}${axisSuffix}`}
            </SvgText>
          </React.Fragment>
        );
      })}

      <SvgLine
        x1={leftPad}
        y1={xAxisY}
        x2={chartWidth - rightPad}
        y2={xAxisY}
        stroke={svgAxisLineColor}
        strokeWidth="1"
      />

      {targetY !== null && (
        <SvgLine
          x1={leftPad}
          y1={targetY}
          x2={chartWidth - rightPad}
          y2={targetY}
          stroke={targetLineColor}
          strokeWidth="1.2"
          strokeDasharray="4 4"
        />
      )}

      {chartType === "bar" ? (
        <>
          {polylinePoints.map((pt, idx) => {
            const slotW = innerWidth / Math.max(points.length, 1);
            const barW = slotW * 0.6;
            const bx = leftPad + idx * slotW + (slotW - barW) / 2;
            const baseline = topPad + innerHeight;
            const barH = Math.max(baseline - pt.y, 0);
            const fillColor =
              typeof targetLineValue === "number" &&
              pt.value < targetLineValue
                ? "#C12C01"
                : lineColor;
            return (
              <Rect
                key={`bar-${idx}`}
                x={bx}
                y={pt.y}
                width={barW}
                height={barH}
                fill={fillColor}
                rx={2}
              />
            );
          })}
          {showPointLabels &&
            polylinePoints.map((pt, idx) => {
              const slotW = innerWidth / Math.max(points.length, 1);
              const bx = leftPad + idx * slotW + slotW / 2;
              const fillColor =
                typeof targetLineValue === "number" &&
                pt.value < targetLineValue
                  ? "#C12C01"
                  : lineColor;
              return (
                <SvgText
                  key={`bar-label-${idx}`}
                  x={bx}
                  y={pt.y - 5}
                  fill={fillColor}
                  fontSize={plotTextFontSize}
                  fontFamily={FontFamily.bold}
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {`${pt.value.toFixed(pointLabelPrecision)}${axisSuffix}`}
                </SvgText>
              );
            })}
          {xLabels.map((label, index) => {
            const slotW = innerWidth / Math.max(xLabels.length, 1);
            const x = leftPad + index * slotW + slotW / 2;
            return (
              <React.Fragment key={`x-label-group-${index}`}>
                <SvgText
                  x={x}
                  y={xAxisPrimaryLabelY}
                  fill={svgAxisLabelColor}
                  fontSize={plotTextFontSize}
                  fontFamily={FontFamily.bold}
                  fontWeight="500"
                  textAnchor="middle"
                >
                  {label}
                </SvgText>
                {xSecondaryLabels && xSecondaryLabels[index] && (
                  <SvgText
                    x={x}
                    y={xAxisSecondaryLabelY}
                    fill={svgAxisLabelColor}
                    fontSize={plotTextFontSize}
                    fontFamily={FontFamily.bold}
                    fontWeight="500"
                    textAnchor="middle"
                  >
                    {xSecondaryLabels[index]}
                  </SvgText>
                )}
              </React.Fragment>
            );
          })}
        </>
      ) : (
        <>
          <Polyline
            points={polylinePoints.map((pt) => `${pt.x},${pt.y}`).join(" ")}
            fill="none"
            stroke={lineColor}
            strokeWidth="2.5"
          />

          {polylinePoints.map((pt, idx) => (
            <Circle
              key={`pt-${idx}`}
              cx={pt.x}
              cy={pt.y}
              r="3"
              fill={pointFillColor ?? lineColor}
              fillOpacity={1}
              stroke={pointStrokeColor ?? lineColor}
              strokeWidth={pointStrokeWidth}
            />
          ))}

          {showPointLabels &&
            polylinePoints.map((pt, idx) => (
              <SvgText
                key={`pt-label-${idx}`}
                x={pt.x}
                y={pt.y - 10}
                fill={lineColor}
                fontSize={plotTextFontSize}
                fontFamily={FontFamily.bold}
                fontWeight="700"
                textAnchor="middle"
              >
                {`${pt.value.toFixed(pointLabelPrecision)}${axisSuffix}`}
              </SvgText>
            ))}

          {xLabels.map((label, index) => {
            const x =
              leftPad +
              (index / Math.max(xLabels.length - 1, 1)) * innerWidth;
            return (
              <React.Fragment key={`x-label-group-${index}`}>
                <SvgText
                  x={x}
                  y={xAxisPrimaryLabelY}
                  fill={svgAxisLabelColor}
                  fontFamily={FontFamily.bold}
                  fontSize={plotTextFontSize}
                  fontWeight="500"
                  textAnchor="middle"
                >
                  {label}
                </SvgText>
                {xSecondaryLabels && xSecondaryLabels[index] && (
                  <SvgText
                    x={x}
                    y={xAxisSecondaryLabelY}
                    fill={svgAxisLabelColor}
                    fontSize={plotTextFontSize}
                    fontFamily={FontFamily.bold}
                    fontWeight="500"
                    textAnchor="middle"
                  >
                    {xSecondaryLabels[index]}
                  </SvgText>
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
    </>
  );

  return (
    <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
      <View style={styles.chartHead}>
        <View style={styles.chartTitleRow}>
          <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>{title}</Text>
          {!!titleQualifier && (
            <Text style={[styles.chartTitleQualifier, isDark && { color: '#B0B0B0' }]}>by {titleQualifier}</Text>
          )}
        </View>
        {!!subtitle && <Text style={[styles.chartSubtitle, isDark && { color: '#B0B0B0' }]}>{subtitle}</Text>}
      </View>
      {needsHorizontalScroll ? (
        <ScrollView horizontal showsHorizontalScrollIndicator>
          <Svg
            width={chartWidth}
            height={chartHeight}
            // Negative left offset reserves room so right-anchored "101%"-style y-axis labels don't clip against the viewBox edge.
            viewBox={`-28 0 ${chartWidth + 28} ${chartHeight}`}
          >
            {svgChildren}
          </Svg>
        </ScrollView>
      ) : (
        <Svg
          width="100%"
          height={chartHeight}
          // Negative left offset reserves room so right-anchored "101%"-style y-axis labels don't clip against the viewBox edge.
          viewBox={`-28 0 ${chartWidth + 28} ${chartHeight}`}
        >
          {svgChildren}
        </Svg>
      )}
    </View>
  );
};

const VarianceChartCard: React.FC<VarianceChartCardProps> = ({
  title,
  titleQualifier,
  subtitle,
  axisLeftLabel,
  axisCenterLabel,
  axisRightLabel,
  maxAbsVariance,
  bars,
  positiveColor,
  valuePrecision = 2,
  maxListHeight = DEFAULT_VARIANCE_LIST_HEIGHT,
  isDark = false,
}) => {
  return (
    <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
      <View style={styles.chartHead}>
        <View style={styles.chartTitleRow}>
          <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>{title}</Text>
          {!!titleQualifier && (
            <Text style={[styles.chartTitleQualifier, isDark && { color: '#B0B0B0' }]}>by {titleQualifier}</Text>
          )}
        </View>
        {!!subtitle && <Text style={[styles.chartSubtitle, isDark && { color: '#B0B0B0' }]}>{subtitle}</Text>}
      </View>

      <ScrollView
        style={{ maxHeight: maxListHeight }}
        showsVerticalScrollIndicator
        nestedScrollEnabled
      >
      {bars.map((item, index) => {
        const magnitude = Math.min(Math.abs(item.variance), maxAbsVariance);
        const widthRatio = magnitude / maxAbsVariance;
        const widthPct = `${widthRatio * 50}%`;
        const positiveValueAnchor = `${50 + widthRatio * 50}%`;
        const negativeValueAnchor = `${50 - widthRatio * 50}%`;
        const isPositive = item.variance >= 0;
        const useInsideNegativeLabel = !isPositive && widthRatio >= 0.75;
        const isFirst = index === 0;
        const isLast = index === bars.length - 1;
        return (
          <View key={item.label} style={styles.varianceRow}>
            <Text style={[styles.varianceRowLabel, isDark && { color: '#B0B0B0' }]} numberOfLines={1}>
              {item.label}
            </Text>
            <View style={styles.varianceTrackWrap}>
              <View style={[styles.varianceTrack, isDark && { backgroundColor: '#2B2B2B' }]}>
                <View
                  style={[
                    styles.varianceCenterLine,
                    isFirst && styles.varianceCenterLineFirst,
                    isLast && styles.varianceCenterLineLast,
                    isDark && { backgroundColor: '#B0B0B0' },
                  ]}
                />
                {isPositive ? (
                  <View
                    style={[
                      styles.varianceBarPositive,
                      { width: widthPct, backgroundColor: positiveColor },
                    ]}
                  />
                ) : (
                  <View
                    style={[styles.varianceBarNegative, { width: widthPct }]}
                  />
                )}
                <Text
                  style={[
                    styles.varianceValueInline,
                    isPositive
                      ? styles.variancePositive
                      : useInsideNegativeLabel
                      ? styles.varianceNegativeOnBar
                      : styles.varianceNegative,
                    isPositive
                      ? { left: positiveValueAnchor, marginLeft: 6 }
                      : useInsideNegativeLabel
                      ? { left: negativeValueAnchor, marginLeft: 6 }
                      : { right: positiveValueAnchor, marginRight: 6 },
                  ]}
                >
                  {`${item.variance > 0 ? "+" : ""}${item.variance.toFixed(
                    valuePrecision
                  )}pp`}
                </Text>
              </View>
            </View>
          </View>
        );
      })}
      </ScrollView>

      <View style={styles.varianceAxisRow}>
        <View style={styles.varianceAxisLabelSpacer} />
        <View style={styles.varianceAxisScaleRow}>
          <Text style={[styles.varianceAxisLabel, isDark && { color: '#B0B0B0' }]}>{axisLeftLabel}</Text>
          <Text style={[styles.varianceAxisCenter, isDark && { color: '#B0B0B0' }]}>{axisCenterLabel}</Text>
          <Text style={[styles.varianceAxisLabel, isDark && { color: '#B0B0B0' }]}>{axisRightLabel}</Text>
        </View>
        <View style={styles.varianceAxisValueSpacer} />
      </View>
    </View>
  );
};

const KpiInfoCardSkeleton: React.FC<{ isDark?: boolean }> = ({ isDark = false }) => {
  const skeletonBg = isDark ? '#2B2B2B' : '#E5E7EB';
  const cardBg = isDark ? '#202020' : undefined;
  const cardBorder = isDark ? { borderColor: '#404040', borderWidth: 1 } : {};
  return (
    <View style={[styles.kpiCard, isDark && { backgroundColor: cardBg }, cardBorder]}>
      <View style={styles.kpiLabelRow}>
        <View style={styles.kpiLabelWrapper}>
          <View style={[styles.kpiLabel, styles.skeletonLoader, { width: "60%", height: 16, backgroundColor: skeletonBg }]} />
          <View style={[styles.infoDot, styles.skeletonLoader, { backgroundColor: skeletonBg }]} />
        </View>
      </View>
      <View style={[styles.kpiValue, styles.skeletonLoader, { width: "40%", height: 32, marginTop: 12, backgroundColor: skeletonBg }]} />
      <View style={[styles.kpiSubText, styles.skeletonLoader, { width: "70%", height: 14, marginTop: 8, backgroundColor: skeletonBg }]} />
    </View>
  );
};

const KpiInfoCard: React.FC<KpiInfoCardProps> = ({
  title,
  value,
  subText,
  tooltip,
  hideTooltipTitle = false,
  valueTone = "neutral",
  onPress,
  isDark = false,
  isSelected = false,
}) => {
  const { colors } = useTheme();
  const tooltipTrigger = Platform.OS === 'web' ? 'hover' : 'click';
  const toneStyle =
    valueTone === "positive"
      ? styles.kpiValuePositive
      : valueTone === "negative"
      ? styles.kpiValueNegative
      : null;
  const darkCardStyle = isDark
    ? { backgroundColor: '#202020', borderColor: '#404040', borderWidth: 1 }
    : {};
  // Selected (Incomplete Loads applied) card appearance — sourced directly
  // from the global theme's `cardSelected` tokens, NOT a hand-maintained
  // Light/Dark hex pair gated by a separate `isDark` check. `colors`
  // already resolves to the correct Light or Dark values for the CURRENT
  // resolved theme (see useTheme()), on every platform, and updates live
  // the moment the user switches theme. Border width is explicit so the
  // selected border is always visible even though the unselected card has
  // no border at all.
  const selectedCardStyle = isSelected
    ? {
        borderWidth: 1,
        borderColor: colors.cardSelected.border,
        backgroundColor: colors.cardSelected.background,
      }
    : null;
  // Selected style is spread last in the container style array below so it
  // takes precedence over the default card style, the dark theme card
  // style, and (being static per-render) any hover/active state applied
  // via activeOpacity.
  //
  // Note: this is plain JSX (not an inline-defined component) — defining a
  // new `const Wrapper: React.FC = () => ...` inside this render would give
  // React a different component type every render, forcing React to
  // unmount/remount the underlying TouchableOpacity/View (and its native
  // view) on every single re-render instead of just updating its props.
  // That doesn't block style application, but it is unnecessary churn and a
  // likely source of visual flicker when the selected style toggles.
  const cardContainerStyle = [
    styles.kpiCard,
    onPress && styles.kpiCardInteractive,
    darkCardStyle,
    selectedCardStyle,
  ];

  const cardContent = (
    <>
      <View style={styles.kpiLabelRow}>
        <View style={styles.kpiLabelWrapper}>
          <Text style={[styles.kpiLabel, isDark && { color: '#B0B0B0' }]}>{title}</Text>
          <DLOTooltip
            title={hideTooltipTitle ? undefined : title}
            content={tooltip}
            position="top"
            trigger={tooltipTrigger}
            maxWidth={420}
          >
            <View style={[styles.infoDot, isDark && { borderColor: '#6E6E6E' }]}>
              <Text style={[styles.infoDotText, isDark && { color: '#6E6E6E' }]}>i</Text>
            </View>
          </DLOTooltip>
        </View>
      </View>
      <Text
        style={[
          styles.kpiValue,
          toneStyle,
          isDark && !toneStyle && { color: '#F2F2F2' },
          // On the selected (Incomplete Loads applied) card, override the
          // negative-tone value text to the theme's `cardSelected.text`
          // token so it stays readable against `cardSelected.background`
          // in BOTH Light and Dark mode (resolved automatically by
          // useTheme() — no separate isDark branch needed here).
          isSelected && valueTone === "negative" && { color: colors.cardSelected.text },
        ]}
      >
        {value}
      </Text>
      <Text style={[styles.kpiSubText, isDark && { color: '#B0B0B0' }]}>{subText}</Text>
    </>
  );

  if (onPress) {
    return (
      <TouchableOpacity
        style={cardContainerStyle}
        onPress={onPress}
        activeOpacity={0.8}
      >
        {cardContent}
      </TouchableOpacity>
    );
  }

  return <View style={cardContainerStyle}>{cardContent}</View>;
};

interface FilteredKpiData {
  plannedLoads: number;
  completedLoads: number;
  onTimeLoads: number;
  incompleteLoads: number;
  manifestArrivalNumerator: number;
  manifestArrivalDenominator: number;
}

interface ExecutiveOverviewTabProps {
  appliedFilters: AppliedFiltersSnapshot;
  adminLocations: any[];
  kpiData: KpiExecutiveOverviewResponse | null;
  kpiLoading: boolean;
  kpiError: string | null;
  onKpiRetry: () => void;
  filteredKpiData: FilteredKpiData;
  /**
   * @deprecated No longer used to derive Dark mode — `isDark` is now
   * derived directly from the global theme's `resolvedTheme` so every
   * platform (web, iPad, mobile phone) stays in sync. Kept optional for
   * backward compatibility with existing call sites.
   */
  isDarkTablet?: boolean;
}

const ExecutiveOverviewTab: React.FC<ExecutiveOverviewTabProps> = ({
  appliedFilters,
  adminLocations,
  kpiData,
  kpiLoading,
  kpiError,
  onKpiRetry,
  filteredKpiData,
  // isDarkTablet intentionally not destructured — see @deprecated note above.
}) => {
  const { showToast } = useToast();
  const { resolvedTheme } = useTheme();
  const { width } = useWindowDimensions();
  // iPad detection (native iOS app running on a tablet-sized screen, as opposed to a phone or
  // desktop/web browser) — follows the same width threshold convention used elsewhere in this
  // module (see NotificationOverlay.tsx's `isIPad`). Used ONLY to stack the chart rows below the
  // KPI grid one-per-row on iPad; desktop (web) and mobile phone layouts are untouched.
  const isIPad = Platform.OS === 'ios' && width >= 768;
  // Derived directly from the global theme (not gated behind a
  // Platform.OS === 'ios' check) so Dark mode is applied consistently on
  // every platform — web, iPad, and native mobile phone — and updates
  // immediately when the user switches theme. The previous
  // `(isMobilePhone && resolvedTheme === 'dark') || isDarkTablet` gate left
  // web (and Android) permanently in Light-mode styling for this tab
  // regardless of the actual resolved theme, which was the root cause of
  // the selected "Incomplete Loads" card's Dark mode colors never
  // appearing on web. `isDarkTablet` is accepted for backward
  // compatibility but is no longer required for correctness.
  const isDark = resolvedTheme === 'dark';

  const [isChartConfigExpanded, setIsChartConfigExpanded] = useState(false);
  const [manualLocationHierarchy, setManualLocationHierarchy] =
    useState<ManualLocationHierarchy>(null);
  const [manualTimeframe, setManualTimeframe] = useState<ManualTimeframe>(null);

  const completedPct = useMemo(() => {
    const value = kpiData?.completedPercentage ?? null;
    if (value != null) {
      return `${value.toFixed(2)}%`;
    }
    return fmtPct(filteredKpiData.completedLoads, filteredKpiData.plannedLoads);
  }, [kpiData?.completedPercentage, filteredKpiData.completedLoads, filteredKpiData.plannedLoads]);

  const onTimePct = useMemo(() => {
    const value = kpiData?.onTimePercentage ?? null;
    if (value != null) {
      return `${value.toFixed(2)}%`;
    }
    return fmtPct(filteredKpiData.onTimeLoads, filteredKpiData.plannedLoads);
  }, [kpiData?.onTimePercentage, filteredKpiData.onTimeLoads, filteredKpiData.plannedLoads]);

  const incompleteLoads = useMemo(() => {
    if (kpiData?.incompleteLoads != null) {
      return kpiData.incompleteLoads;
    }
    return filteredKpiData.incompleteLoads;
  }, [kpiData?.incompleteLoads, filteredKpiData.incompleteLoads]);

  const driverArrivedPct = useMemo(() => {
    const value = kpiData?.driverArrivedAsExpectedPercentage ?? null;
    if (value != null) {
      return `${value.toFixed(2)}%`;
    }
    return fmtPct(
      filteredKpiData.manifestArrivalNumerator,
      filteredKpiData.manifestArrivalDenominator
    );
  }, [kpiData?.driverArrivedAsExpectedPercentage, filteredKpiData.manifestArrivalNumerator, filteredKpiData.manifestArrivalDenominator]);

  const completedTone: "positive" | "negative" =
    Number(completedPct.replace("%", "")) >= KPI_TARGETS.targetCompletedPct
      ? "positive"
      : "negative";
  const onTimeTone: "positive" | "negative" =
    Number(onTimePct.replace("%", "")) >= KPI_TARGETS.targetOnTimePct
      ? "positive"
      : "negative";
  const daeTone: "positive" | "negative" =
    Number(driverArrivedPct.replace("%", "")) >= KPI_TARGETS.targetDriverArrivedPct
      ? "positive"
      : "negative";

  const autoLocationHierarchy = useMemo(
    () => inferLocationHierarchy(appliedFilters),
    [appliedFilters]
  );

  const autoTimeframe = useMemo(
    () => inferTimeframe(appliedFilters),
    [appliedFilters]
  );

  const effectiveLocationHierarchy = manualLocationHierarchy || autoLocationHierarchy;
  const effectiveTimeframe = manualTimeframe || autoTimeframe;

  const isAutoDrillDownActive = useMemo(
    () => manualLocationHierarchy === null && manualTimeframe === null,
    [manualLocationHierarchy, manualTimeframe]
  );

  const hierarchyLabels = useMemo(() => {
    const appliedRegionIds = new Set(
      appliedFilters.selectedRegions.map((r) => normalizeFilterValue(r.id))
    );
    const appliedMarketIds = new Set(
      appliedFilters.selectedMarkets.map((m) => normalizeFilterValue(m.id))
    );
    const appliedLocationIds = new Set(
      appliedFilters.selectedLocations.map((l) => normalizeFilterValue(l.id))
    );

    const scopedLocations = adminLocations.filter((loc) => {
      const regionOk =
        appliedRegionIds.size === 0 ||
        appliedRegionIds.has(normalizeFilterValue(getRegionLabel(loc)));
      const marketOk =
        appliedMarketIds.size === 0 ||
        appliedMarketIds.has(normalizeFilterValue(getMarketLabel(loc)));
      const locationOk =
        appliedLocationIds.size === 0 ||
        appliedLocationIds.has(normalizeFilterValue(loc.locationId)) ||
        appliedLocationIds.has(normalizeFilterValue(loc.locId)) ||
        appliedLocationIds.has(normalizeFilterValue(getLocationLabel(loc)));
      return regionOk && marketOk && locationOk;
    });

    let labels: string[];
    if (effectiveLocationHierarchy === "region") {
      labels = Array.from(
        new Set(
          scopedLocations.map((loc) => getRegionLabel(loc)).filter(Boolean)
        )
      );
    } else if (effectiveLocationHierarchy === "market") {
      labels = Array.from(
        new Set(
          scopedLocations.map((loc) => getMarketLabel(loc)).filter(Boolean)
        )
      );
    } else {
      labels = Array.from(
        new Set(
          scopedLocations.map((loc) => getLocationLabel(loc)).filter(Boolean)
        )
      );
    }

    const fallback =
      effectiveLocationHierarchy === "region"
        ? ["Atlantic", "Canada", "Northeast", "Southeast", "West"]
        : effectiveLocationHierarchy === "market"
        ? [
            "Mid-Atlantic",
            "Great Lakes",
            "Florida",
            "Northern California",
            "Desert Mountain",
          ]
        : [
            "Baltimore MD",
            "Dallas TX",
            "Toronto ON",
            "Seattle WA",
            "Phoenix AZ",
          ];

    const selected = (labels.length ? labels : fallback).slice(
      0,
      effectiveLocationHierarchy === "location" ? 8 : 6
    );
    return selected;
  }, [
    adminLocations,
    appliedFilters.selectedLocations,
    appliedFilters.selectedMarkets,
    appliedFilters.selectedRegions,
    effectiveLocationHierarchy,
  ]);

  const overviewNoDataMessage = "No data for the current filters";

  const {
    data: completedTrendResponse,
    loading: completedTrendLoading,
    error: completedTrendError,
    noData: completedTrendNoData,
    refetch: refetchCompletedTrend,
  } = useCompletedTrend(appliedFilters, effectiveTimeframe);

  const completedTrendPoints = completedTrendResponse?.data ?? [];
  const completedTrendAxisRange = useMemo(
    () =>
      getDynamicTrendAxisRange(
        completedTrendPoints,
        completedTrendResponse?.target,
        93,
        101
      ),
    [completedTrendPoints, completedTrendResponse?.target]
  );

  const {
    data: onTimeTrendResponse,
    loading: onTimeTrendLoading,
    error: onTimeTrendError,
    noData: onTimeTrendNoData,
    refetch: refetchOnTimeTrend,
  } = useOnTimeTrend(appliedFilters, effectiveTimeframe);

  const onTimeTrendPoints = onTimeTrendResponse?.data ?? [];
  const onTimeTrendAxisRange = useMemo(
    () =>
      getDynamicTrendAxisRange(
        onTimeTrendPoints,
        onTimeTrendResponse?.target,
        93,
        101
      ),
    [onTimeTrendPoints, onTimeTrendResponse?.target]
  );

  const {
    data: driverArrivedTrendResponse,
    loading: driverArrivedTrendLoading,
    error: driverArrivedTrendError,
    noData: driverArrivedTrendNoData,
    refetch: refetchDriverArrivedTrend,
  } = useDriverArrivedTrend(appliedFilters, effectiveTimeframe);

  const driverArrivedTrendPoints = driverArrivedTrendResponse?.data ?? [];
  const driverArrivedTrendAxisRange = useMemo(
    () =>
      getDynamicTrendAxisRange(
        driverArrivedTrendPoints,
        driverArrivedTrendResponse?.target,
        78,
        92
      ),
    [driverArrivedTrendPoints, driverArrivedTrendResponse?.target]
  );

  const {
    data: completedVarianceResponse,
    loading: completedVarianceLoading,
    error: completedVarianceError,
    noData: completedVarianceNoData,
    refetch: refetchCompletedVariance,
  } = useCompletedVariance(appliedFilters, effectiveLocationHierarchy);

  // API already returns entities pre-sorted by variance; do not re-sort here.
  const completedVarianceBars: VariancePoint[] = useMemo(
    () =>
      (completedVarianceResponse?.data ?? [])
        .filter((entity) => !!entity.entityName)
        .map((entity) => ({
          label: entity.entityName,
          variance: entity.variancePp,
        })),
    [completedVarianceResponse]
  );

  const completedVarianceMaxAbs = completedVarianceResponse?.maxAbsoluteVariance ?? 2.0;
  const completedVarianceTarget = completedVarianceResponse?.target ?? 99.5;

  const {
    data: onTimeVarianceResponse,
    loading: onTimeVarianceLoading,
    error: onTimeVarianceError,
    noData: onTimeVarianceNoData,
    refetch: refetchOnTimeVariance,
  } = useOnTimeVariance(appliedFilters, effectiveLocationHierarchy);

  // API already returns entities pre-sorted by variance; do not re-sort here.
  const onTimeVarianceBars: VariancePoint[] = useMemo(
    () =>
      (onTimeVarianceResponse?.data ?? [])
        .filter((entity) => !!entity.entityName)
        .map((entity) => ({
          label: entity.entityName,
          variance: entity.variancePp,
        })),
    [onTimeVarianceResponse]
  );

  const onTimeVarianceMaxAbs = onTimeVarianceResponse?.maxAbsoluteVariance ?? 3.4;
  const onTimeVarianceTarget = onTimeVarianceResponse?.target ?? 98.5;

  const {
    data: driverArrivalVarianceResponse,
    loading: driverArrivalVarianceLoading,
    error: driverArrivalVarianceError,
    noData: driverArrivalVarianceNoData,
    refetch: refetchDriverArrivalVariance,
  } = useDriverArrivalVariance(appliedFilters, effectiveLocationHierarchy);

  // API already returns entities pre-sorted by variance; do not re-sort here.
  const driverArrivalVarianceBars: VariancePoint[] = useMemo(
    () =>
      (driverArrivalVarianceResponse?.data ?? [])
        .filter((entity) => !!entity.entityName)
        .map((entity) => ({
          label: entity.entityName,
          variance: entity.variancePp,
        })),
    [driverArrivalVarianceResponse]
  );

  const driverArrivalVarianceMaxAbs = driverArrivalVarianceResponse?.maxAbsoluteVariance ?? 4.6;
  const driverArrivalVarianceTarget = driverArrivalVarianceResponse?.target ?? 85.0;

  const handleResetToAuto = () => {
    setManualLocationHierarchy(null);
    setManualTimeframe(null);
    showToast({
      header: "Chart Configuration",
      subtext: "Chart Configuration reset to auto drill-down",
      variant: "success",
      duration: 3000,
    });
  };

  const handleHierarchySelect = (hierarchy: ChartHierarchyLevel) => {
    setManualLocationHierarchy(hierarchy);
  };

  const handleTimeframeSelect = (timeframe: ChartTimeframeGrain) => {
    setManualTimeframe(timeframe);
  };

  return (
    <>
      <View style={styles.chartConfigOuter}>
        <View style={styles.chartConfigWrapper}>
          <View
            style={[
              styles.chartConfigCard,
              isDark && { backgroundColor: '#202020', borderColor: '#404040', borderWidth: 1 },
            ]}
          >
            <TouchableOpacity
              style={[
                styles.chartConfigHeader,
                isDark && { backgroundColor: '#202020' },
              ]}
              onPress={() => setIsChartConfigExpanded((prev) => !prev)}
              accessibilityRole="button"
              accessibilityLabel="Chart Configuration"
              aria-expanded={isChartConfigExpanded}
            >
              <View style={styles.chartConfigTitleRow}>
                <MaterialIcons name="tune" size={18} color={isDark ? '#3D8BFF' : '#005CBC'} />
                <Text style={[styles.chartConfigLabel, isDark && { color: '#F2F2F2' }]}>
                  Chart Configuration
                </Text>
                <View style={[styles.chartConfigCountPill, isDark && { backgroundColor: '#3D8BFF' }]}>
                  <Text style={styles.chartConfigCountText}>2 / 2</Text>
                </View>
                <Text style={[styles.chartConfigDescription, isDark && { color: '#B0B0B0' }]} numberOfLines={1}>
                  Grouping and timeframe for all charts on this tab
                </Text>
              </View>
              <MaterialIcons
                name="keyboard-arrow-down"
                size={24}
                color={isDark ? '#B0B0B0' : '#616161'}
                style={[
                  styles.chartConfigChevron,
                  isChartConfigExpanded && styles.chartConfigChevronExpanded,
                ]}
              />
            </TouchableOpacity>

            {isChartConfigExpanded && (
              <View
                style={[
                  styles.chartConfigBody,
                  isDark && { borderTopColor: '#363636', backgroundColor: '#202020' },
                ]}
              >
                <View style={styles.chartConfigGroup}>
                  <Text style={[styles.chartConfigGroupLabel, isDark && { color: '#B0B0B0' }]}>
                    Location Hierarchy
                  </Text>
                  <View style={[styles.segmentedControl, isDark && { borderColor: '#404040' }]}>
                    {CHART_HIERARCHY_OPTIONS.map((opt, idx) => {
                      const isActive = effectiveLocationHierarchy === opt.key;
                      return (
                        <TouchableOpacity
                          key={opt.key}
                          style={[
                            styles.segmentedOption,
                            isDark && { backgroundColor: '#2B2B2B' },
                            idx > 0 && styles.segmentedOptionDivider,
                            idx > 0 && isDark && { borderLeftColor: '#404040' },
                            isActive && styles.segmentedOptionActive,
                            isActive && isDark && { backgroundColor: '#3D8BFF' },
                          ]}
                          onPress={() => handleHierarchySelect(opt.key)}
                          accessibilityRole="button"
                          accessibilityLabel={opt.label}
                          aria-selected={isActive}
                        >
                          <Text
                            style={[
                              styles.segmentedOptionText,
                              isDark && { color: '#B0B0B0' },
                              isActive && styles.segmentedOptionTextActive,
                            ]}
                          >
                            {opt.label}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>

                <View style={styles.chartConfigGroup}>
                  <Text style={[styles.chartConfigGroupLabel, isDark && { color: '#B0B0B0' }]}>
                    Timeframe
                  </Text>
                  <View style={styles.chartConfigTimeframeRow}>
                    <View style={[styles.segmentedControl, isDark && { borderColor: '#404040' }]}>
                      {CHART_TIMEFRAME_OPTIONS.map((opt, idx) => {
                        const isActive = effectiveTimeframe === opt.key;
                        return (
                          <TouchableOpacity
                            key={opt.key}
                            style={[
                              styles.segmentedOption,
                              isDark && { backgroundColor: '#2B2B2B' },
                              idx > 0 && styles.segmentedOptionDivider,
                              idx > 0 && isDark && { borderLeftColor: '#404040' },
                              isActive && styles.segmentedOptionActive,
                              isActive && isDark && { backgroundColor: '#3D8BFF' },
                            ]}
                            onPress={() => handleTimeframeSelect(opt.key)}
                            accessibilityRole="button"
                            accessibilityLabel={opt.label}
                            aria-selected={isActive}
                          >
                            <Text
                              style={[
                                styles.segmentedOptionText,
                                isDark && { color: '#B0B0B0' },
                                isActive && styles.segmentedOptionTextActive,
                              ]}
                            >
                              {opt.label}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                    {isAutoDrillDownActive && (
                      <Text style={[styles.autoDrillDownActiveText, isDark && { color: '#3D8BFF' }]}>
                        Auto drill-down active
                      </Text>
                    )}
                  </View>
                </View>

                {!isAutoDrillDownActive && (
                  <TouchableOpacity
                    style={[
                      styles.resetToAutoButton,
                      styles.resetToAutoButtonRight,
                      isDark && { backgroundColor: '#2B2B2B' },
                    ]}
                    onPress={handleResetToAuto}
                    accessibilityRole="button"
                    accessibilityLabel="Reset to auto"
                  >
                    <Text style={[styles.resetToAutoButtonText, isDark && { color: '#3D8BFF' }]}>
                      Reset to auto
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        </View>
      </View>

      <View style={styles.tabContent}>
        <View>
          {kpiLoading ? (
            <View style={styles.kpiGrid}>
              <KpiInfoCardSkeleton isDark={isDark} />
              <KpiInfoCardSkeleton isDark={isDark} />
              <KpiInfoCardSkeleton isDark={isDark} />
              <KpiInfoCardSkeleton isDark={isDark} />
            </View>
          ) : kpiError ? (
            <View style={styles.kpiErrorWrapper}>
              <View style={styles.kpiGrid}>
                <KpiInfoCardSkeleton isDark={isDark} />
                <KpiInfoCardSkeleton isDark={isDark} />
                <KpiInfoCardSkeleton isDark={isDark} />
                <KpiInfoCardSkeleton isDark={isDark} />
              </View>
              <View
                style={styles.kpiRetryOverlay}
                pointerEvents="box-none"
              >
                <TouchableOpacity
                  style={[styles.retryButton, isDark && { backgroundColor: '#3D8BFF' }]}
                  onPress={onKpiRetry}
                  accessibilityRole="button"
                  accessibilityLabel="Retry loading KPI data"
                >
                  <Text style={styles.retryButtonText}>Retry</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <View style={styles.kpiGrid}>
              <KpiInfoCard
                title="Completed %"
                value={completedPct}
                valueTone={completedTone}
                subText={
                  kpiData
                    ? `${formatCount(kpiData.completedLoads)} of ${formatCount(kpiData.plannedLoads)} planned loads`
                    : `${formatCount(filteredKpiData.completedLoads)} of ${formatCount(
                        filteredKpiData.plannedLoads
                      )} planned loads`
                }
                tooltip="Completed Loads divided by Planned Loads. A load is complete when its final Warehouse Load Time is populated. Target 99.50%."
                hideTooltipTitle
                isDark={isDark}
              />
              <KpiInfoCard
                title="On-Time %"
                value={onTimePct}
                valueTone={onTimeTone}
                subText={
                  kpiData
                    ? `${formatCount(kpiData.onTimeLoads)} of ${formatCount(kpiData.plannedLoads)} planned loads`
                    : `${formatCount(filteredKpiData.onTimeLoads)} of ${formatCount(
                        filteredKpiData.plannedLoads
                      )} planned loads`
                }
                tooltip="On-Time Loads divided by Planned Loads. On time when the final Warehouse Load Time is before the actual Route Start Time. Target 98.50%."
                hideTooltipTitle
                isDark={isDark}
              />
              <KpiInfoCard
                title="Incomplete Loads"
                value={formatCount(incompleteLoads)}
                valueTone={incompleteLoads > 0 ? "negative" : "positive"}
                subText={incompleteLoads === 0 ? "No open loads" : "Loads with open SSCCs"}
                tooltip="Loads with open SSCCs."
                hideTooltipTitle
                isDark={isDark}
              />
              <KpiInfoCard
                title="Driver Arrived As Expected"
                value={driverArrivedPct}
                valueTone={daeTone}
                subText={
                  kpiData
                    ? `${formatCount(kpiData.driverArrivedAsExpectedCount)} of ${formatCount(
                        kpiData.driverManifestCount
                      )} manifest arrivals`
                    : `${formatCount(
                        filteredKpiData.manifestArrivalNumerator
                      )} of ${formatCount(
                        filteredKpiData.manifestArrivalDenominator
                      )} manifest arrivals`
                }
                tooltip="Drivers arriving at or before their Manifest Arrival Time divided by drivers with a populated Manifest Arrival Time. Blank manifest times are excluded from both measures. Target 85.00%."
                hideTooltipTitle
                isDark={isDark}
              />
            </View>
          )}

          {/* Dark-mode overrides for chart area — only on mobile phone */}
          <View style={[styles.chartPairGrid, isIPad && styles.chartPairGridStacked]}>
            <View style={[styles.chartPairItemWide, isIPad && styles.chartPairItemFullWidth]}>
              {completedTrendLoading ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Completed % Trend</Text>
                    </View>
                  </View>
                  <View style={[styles.chartSkeletonPlot, styles.skeletonLoader, isDark && { backgroundColor: '#2B2B2B' }]} />
                </View>
              ) : completedTrendError ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Completed % Trend</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartErrorText, isDark && { color: '#F87171' }]}>{completedTrendError}</Text>
                    <TouchableOpacity
                      style={[styles.retryButton, isDark && { backgroundColor: '#3D8BFF' }]}
                      onPress={refetchCompletedTrend}
                      accessibilityRole="button"
                      accessibilityLabel="Retry Completed % Trend"
                    >
                      <Text style={styles.retryButtonText}>Retry</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : completedTrendNoData ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Completed % Trend</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartEmptyText, isDark && { color: '#B0B0B0' }]}>
                      {overviewNoDataMessage}
                    </Text>
                  </View>
                </View>
              ) : (
                <LineTrendCard
                  title="Completed % Trend"
                  isDark={isDark}
                  titleQualifier={
                    CHART_TIMEFRAME_OPTIONS.find(
                      (o) => o.key === effectiveTimeframe
                    )?.label ?? "Period"
                  }
                  subtitle={completedTrendResponse?.subtitle ?? "Target 99.5%"}
                  xLabels={completedTrendPoints.map((point) => point.label)}
                  xSecondaryLabels={completedTrendPoints.map(trendSecondaryLabel).filter(Boolean) as string[]}
                  yMin={completedTrendAxisRange.min}
                  yMax={completedTrendAxisRange.max}
                  points={completedTrendPoints.map((point) => point.value)}
                  lineColor="#005CBC"
                  targetLineValue={completedTrendResponse?.target ?? 99.5}
                  showPointLabels
                  pointLabelPrecision={1}
                  pointFillColor="#FFFFFF"
                  pointStrokeColor="#005CBC"
                  pointStrokeWidth={2}
                  plotLeftPad={22}
                  plotRightPad={22}
                  plotTopPad={20}
                  plotBottomPad={20}
                  plotWidth={1075}
                  plotHeight={230}
                  plotTextFontSize={11}
                />
              )}
            </View>
            <View style={[styles.chartPairItemNarrow, isIPad && styles.chartPairItemFullWidth]}>
              {completedVarianceLoading ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Completed Variance</Text>
                    </View>
                  </View>
                  <View style={[styles.chartSkeletonPlot, styles.skeletonLoader, isDark && { backgroundColor: '#2B2B2B' }]} />
                </View>
              ) : completedVarianceError ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Completed Variance</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartErrorText, isDark && { color: '#F87171' }]}>{completedVarianceError}</Text>
                    <TouchableOpacity
                      style={[styles.retryButton, isDark && { backgroundColor: '#3D8BFF' }]}
                      onPress={refetchCompletedVariance}
                      accessibilityRole="button"
                      accessibilityLabel="Retry Completed Variance"
                    >
                      <Text style={styles.retryButtonText}>Retry</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : completedVarianceNoData ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Completed Variance</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartEmptyText, isDark && { color: '#B0B0B0' }]}>
                      {overviewNoDataMessage}
                    </Text>
                  </View>
                </View>
              ) : (
                <VarianceChartCard
                  title="Completed Variance"
                  isDark={isDark}
                  titleQualifier={
                    CHART_HIERARCHY_OPTIONS.find(
                      (o) => o.key === effectiveLocationHierarchy
                    )?.label ?? "Region / Unit"
                  }
                  subtitle={completedVarianceResponse?.subtitle ?? `vs ${completedVarianceTarget}% target`}
                  axisLeftLabel={`-${completedVarianceMaxAbs.toFixed(1)}pp`}
                  axisCenterLabel={`TARGET ${completedVarianceTarget.toFixed(1)}%`}
                  axisRightLabel={`+${completedVarianceMaxAbs.toFixed(1)}pp`}
                  maxAbsVariance={completedVarianceMaxAbs}
                  bars={completedVarianceBars}
                  positiveColor="#005CBC"
                  valuePrecision={1}
                />
              )}
            </View>
          </View>

          <View style={[styles.chartPairGrid, isIPad && styles.chartPairGridStacked]}>
            <View style={[styles.chartPairItemWide, isIPad && styles.chartPairItemFullWidth]}>
              {onTimeTrendLoading ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>On-Time % Trend</Text>
                    </View>
                  </View>
                  <View style={[styles.chartSkeletonPlot, styles.skeletonLoader, isDark && { backgroundColor: '#2B2B2B' }]} />
                </View>
              ) : onTimeTrendError ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>On-Time % Trend</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartErrorText, isDark && { color: '#F87171' }]}>{onTimeTrendError}</Text>
                    <TouchableOpacity
                      style={[styles.retryButton, isDark && { backgroundColor: '#3D8BFF' }]}
                      onPress={refetchOnTimeTrend}
                      accessibilityRole="button"
                      accessibilityLabel="Retry On-Time % Trend"
                    >
                      <Text style={styles.retryButtonText}>Retry</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : onTimeTrendNoData ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>On-Time % Trend</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartEmptyText, isDark && { color: '#B0B0B0' }]}>
                      {overviewNoDataMessage}
                    </Text>
                  </View>
                </View>
              ) : (
                <LineTrendCard
                  title="On-Time % Trend"
                  isDark={isDark}
                  titleQualifier={
                    CHART_TIMEFRAME_OPTIONS.find(
                      (o) => o.key === effectiveTimeframe
                    )?.label ?? "Period"
                  }
                  subtitle={onTimeTrendResponse?.subtitle ?? "Target 98.5%"}
                  xLabels={onTimeTrendPoints.map((point) => point.label)}
                  xSecondaryLabels={onTimeTrendPoints.map(trendSecondaryLabel).filter(Boolean) as string[]}
                  yMin={onTimeTrendAxisRange.min}
                  yMax={onTimeTrendAxisRange.max}
                  points={onTimeTrendPoints.map((point) => point.value)}
                  lineColor="#005CBC"
                  targetLineValue={onTimeTrendResponse?.target ?? 98.5}
                  showPointLabels
                  pointLabelPrecision={1}
                  pointFillColor="#FFFFFF"
                  pointStrokeColor="#005CBC"
                  pointStrokeWidth={2}
                  plotLeftPad={22}
                  plotRightPad={22}
                  plotTopPad={20}
                  plotBottomPad={20}
                  plotWidth={1075}
                  plotHeight={230}
                  plotTextFontSize={11}
                />
              )}
            </View>
            <View style={[styles.chartPairItemNarrow, isIPad && styles.chartPairItemFullWidth]}>
              {onTimeVarianceLoading ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>On-Time Variance</Text>
                    </View>
                  </View>
                  <View style={[styles.chartSkeletonPlot, styles.skeletonLoader, isDark && { backgroundColor: '#2B2B2B' }]} />
                </View>
              ) : onTimeVarianceError ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>On-Time Variance</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartErrorText, isDark && { color: '#F87171' }]}>{onTimeVarianceError}</Text>
                    <TouchableOpacity
                      style={[styles.retryButton, isDark && { backgroundColor: '#3D8BFF' }]}
                      onPress={refetchOnTimeVariance}
                      accessibilityRole="button"
                      accessibilityLabel="Retry On-Time Variance"
                    >
                      <Text style={styles.retryButtonText}>Retry</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : onTimeVarianceNoData ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>On-Time Variance</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartEmptyText, isDark && { color: '#B0B0B0' }]}>
                      {overviewNoDataMessage}
                    </Text>
                  </View>
                </View>
              ) : (
                <VarianceChartCard
                  title="On-Time Variance"
                  isDark={isDark}
                  titleQualifier={
                    CHART_HIERARCHY_OPTIONS.find(
                      (o) => o.key === effectiveLocationHierarchy
                    )?.label ?? "Region / Unit"
                  }
                  subtitle={onTimeVarianceResponse?.subtitle ?? `vs ${onTimeVarianceTarget}% target`}
                  axisLeftLabel={`-${onTimeVarianceMaxAbs.toFixed(1)}pp`}
                  axisCenterLabel={`TARGET ${onTimeVarianceTarget.toFixed(1)}%`}
                  axisRightLabel={`+${onTimeVarianceMaxAbs.toFixed(1)}pp`}
                  maxAbsVariance={onTimeVarianceMaxAbs}
                  bars={onTimeVarianceBars}
                  positiveColor="#005CBC"
                  valuePrecision={1}
                />
              )}
            </View>
          </View>

          <View style={[styles.chartPairGrid, isIPad && styles.chartPairGridStacked]}>
            <View style={[styles.chartPairItemWide, isIPad && styles.chartPairItemFullWidth]}>
              {driverArrivedTrendLoading ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Driver Arrived as Expected % Trend</Text>
                    </View>
                  </View>
                  <View style={[styles.chartSkeletonPlot, styles.skeletonLoader, isDark && { backgroundColor: '#2B2B2B' }]} />
                </View>
              ) : driverArrivedTrendError ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Driver Arrived as Expected % Trend</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartErrorText, isDark && { color: '#F87171' }]}>{driverArrivedTrendError}</Text>
                    <TouchableOpacity
                      style={[styles.retryButton, isDark && { backgroundColor: '#3D8BFF' }]}
                      onPress={refetchDriverArrivedTrend}
                      accessibilityRole="button"
                      accessibilityLabel="Retry Driver Arrived as Expected % Trend"
                    >
                      <Text style={styles.retryButtonText}>Retry</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : driverArrivedTrendNoData ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Driver Arrived as Expected % Trend</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartEmptyText, isDark && { color: '#B0B0B0' }]}>
                      {overviewNoDataMessage}
                    </Text>
                  </View>
                </View>
              ) : (
                <LineTrendCard
                  title="Driver Arrived as Expected % Trend"
                  isDark={isDark}
                  titleQualifier={
                    CHART_TIMEFRAME_OPTIONS.find(
                      (o) => o.key === effectiveTimeframe
                    )?.label ?? "Period"
                  }
                  subtitle={driverArrivedTrendResponse?.subtitle ?? "Target 85%"}
                  xLabels={driverArrivedTrendPoints.map((point) => point.label)}
                  xSecondaryLabels={driverArrivedTrendPoints.map(trendSecondaryLabel).filter(Boolean) as string[]}
                  yMin={driverArrivedTrendAxisRange.min}
                  yMax={driverArrivedTrendAxisRange.max}
                  points={driverArrivedTrendPoints.map((point) => point.value)}
                  lineColor="#005CBC"
                  targetLineValue={driverArrivedTrendResponse?.target ?? 85}
                  targetLineColor="#B39DDB"
                  showPointLabels
                  pointLabelPrecision={1}
                  pointFillColor="#FFFFFF"
                  pointStrokeColor="#005CBC"
                  pointStrokeWidth={2}
                  plotLeftPad={22}
                  plotRightPad={22}
                  plotTopPad={20}
                  plotBottomPad={20}
                  plotWidth={1075}
                  plotHeight={230}
                  plotTextFontSize={11}
                />
              )}
            </View>
            <View style={[styles.chartPairItemNarrow, isIPad && styles.chartPairItemFullWidth]}>
              {driverArrivalVarianceLoading ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Driver Arrival Variance</Text>
                    </View>
                  </View>
                  <View style={[styles.chartSkeletonPlot, styles.skeletonLoader, isDark && { backgroundColor: '#2B2B2B' }]} />
                </View>
              ) : driverArrivalVarianceError ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Driver Arrival Variance</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartErrorText, isDark && { color: '#F87171' }]}>{driverArrivalVarianceError}</Text>
                    <TouchableOpacity
                      style={[styles.retryButton, isDark && { backgroundColor: '#3D8BFF' }]}
                      onPress={refetchDriverArrivalVariance}
                      accessibilityRole="button"
                      accessibilityLabel="Retry Driver Arrival Variance"
                    >
                      <Text style={styles.retryButtonText}>Retry</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : driverArrivalVarianceNoData ? (
                <View style={[styles.chartCard, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                  <View style={styles.chartHead}>
                    <View style={styles.chartTitleRow}>
                      <Text style={[styles.chartTitle, isDark && { color: '#F2F2F2' }]}>Driver Arrival Variance</Text>
                    </View>
                  </View>
                  <View style={styles.chartCenterState}>
                    <Text style={[styles.chartEmptyText, isDark && { color: '#B0B0B0' }]}>
                      {overviewNoDataMessage}
                    </Text>
                  </View>
                </View>
              ) : (
                <VarianceChartCard
                  title="Driver Arrival Variance"
                  isDark={isDark}
                  titleQualifier={
                    CHART_HIERARCHY_OPTIONS.find(
                      (o) => o.key === effectiveLocationHierarchy
                    )?.label ?? "Region / Unit"
                  }
                  subtitle={driverArrivalVarianceResponse?.subtitle ?? `vs ${driverArrivalVarianceTarget}% target`}
                  axisLeftLabel={`-${driverArrivalVarianceMaxAbs.toFixed(1)}pp`}
                  axisCenterLabel={`TARGET ${driverArrivalVarianceTarget.toFixed(1)}%`}
                  axisRightLabel={`+${driverArrivalVarianceMaxAbs.toFixed(1)}pp`}
                  maxAbsVariance={driverArrivalVarianceMaxAbs}
                  bars={driverArrivalVarianceBars}
                  positiveColor="#005CBC"
                  valuePrecision={1}
                />
              )}
            </View>
          </View>
        </View>
      </View>
    </>
  );
};

export default ExecutiveOverviewTab;

const styles = StyleSheet.create({
  chartConfigOuter: {
    paddingHorizontal: 24,
  },
  tabContent: {
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 10,
  },
  chartConfigWrapper: {
    paddingBottom: 16,
  },
  chartConfigCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  chartConfigHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingVertical: 12,
    gap: 12,
  },
  chartConfigTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  chartConfigLabel: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: "#2D2D2D",
  },
  chartConfigCountPill: {
    backgroundColor: "#0B5FC6",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 14,
  },
  chartConfigCountText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    color: "#FFFFFF",
  },
  chartConfigDescription: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: "#838383",
    flexShrink: 1,
  },
  chartConfigChevron: {
    transform: [{ rotate: "0deg" }],
  },
  chartConfigChevronExpanded: {
    transform: [{ rotate: "180deg" }],
  },
  chartConfigBody: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    columnGap: 32,
    rowGap: 16,
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    marginHorizontal: 16,
    paddingTop: 16,
  },
  chartConfigGroup: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
  },
  chartConfigGroupLabel: {
    fontSize: 12,
    fontWeight: "700",
    fontFamily: FontFamily.bold,
    color: "#6A6D71",
    letterSpacing: 0.4,
  },
  segmentedControl: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 8,
    overflow: "hidden",
  },
  segmentedOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: "#F2F4F7",
  },
  segmentedOptionDivider: {
    borderLeftWidth: 1,
    borderLeftColor: "#D0D5DD",
  },
  segmentedOptionActive: {
    backgroundColor: "#0B5FC6",
  },
  segmentedOptionText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: "#495057",
  },
  segmentedOptionTextActive: {
    color: "#FFFFFF",
    fontFamily: FontFamily.bold,
  },
  chartConfigTimeframeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  autoDrillDownActiveText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: "#0B5FC6",
    fontWeight: "500",
  },
  resetToAutoButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: "#E3E9F3",
  },
  resetToAutoButtonRight: {
    marginLeft: "auto" as any,
  },
  resetToAutoButtonText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: "#0B5FC6",
    fontWeight: "500",
  },
  kpiGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 14,
    marginBottom: 16,
  },
  kpiErrorCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    marginBottom: 16,
  },
  kpiErrorWrapper: {
    position: "relative",
  },
  kpiRetryOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.65)",
  },
  kpiCard: {
    minWidth: 250,
    flexGrow: 1,
    flexBasis: "23%",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  kpiCardInteractive: {},
  // Note: the selected-card appearance (Incomplete Loads applied) is no
  // longer a static StyleSheet entry here — it's computed inline in
  // KpiInfoCard directly from the global theme's `colors.cardSelected`
  // tokens (see useTheme()) so Light/Dark values can never drift apart.
  kpiLabelWrapper: {
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
  },
  kpiLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
  },
  kpiLabel: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    color: "#6A6D71",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    flexShrink: 1,
  },
  infoDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#9CA3AF",
    alignItems: "center",
    justifyContent: "center",
  },
  infoDotText: {
    fontSize: 10,
    color: "#9CA3AF",
    fontFamily: FontFamily.bold,
    lineHeight: 10,
  },
  kpiValue: {
    fontSize: 32,
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    color: "#1F2937",
  },
  kpiValuePositive: {
    color: "#005CBC",
  },
  kpiValueNegative: {
    color: "#C12C01",
  },
  kpiSubText: {
    marginTop: 6,
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: "#6B7280",
  },
  chartPairGrid: {
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 14,
  },
  chartPairItemWide: {
    minWidth: 540,
    flexGrow: 2,
    flexBasis: "66%",
  },
  chartPairItemNarrow: {
    minWidth: 260,
    flexGrow: 1,
    flexBasis: "34%",
  },
  // iPad-only: stacks each chart row's children vertically (one component per row, full width)
  // instead of the default side-by-side wide/narrow pair. Desktop (web) and mobile phone layouts
  // are unaffected since this is only applied when isIPad is true.
  chartPairGridStacked: {
    flexDirection: "column",
  },
  chartPairItemFullWidth: {
    minWidth: 0,
    width: "100%",
    flexBasis: "100%",
    flexGrow: 1,
  },
  chartCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 22,
    paddingVertical: 20,
    flex: 1,
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  chartHead: {
    marginBottom: 14,
  },
  chartTitleRow: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 6,
  },
  chartTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    color: "#1F2937",
  },
  chartTitleQualifier: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: "#374151",
  },
  chartSubtitle: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: "#6B7280",
    marginTop: 2,
  },
  varianceAxisRow: {
    flexDirection: "row",
    alignItems: "center",
    height: VARIANCE_AXIS_ROW_HEIGHT,
    marginTop: VARIANCE_AXIS_ROW_MARGIN_TOP,
  },
  varianceAxisLabelSpacer: {
    width: 118,
  },
  varianceAxisScaleRow: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: 0,
    paddingRight: 44,
  },
  varianceAxisValueSpacer: {
    width: 0,
  },
  varianceAxisLabel: {
    fontSize: 11,
    color: "#6B7280",
    fontFamily: FontFamily.medium,
  },
  varianceAxisCenter: {
    fontSize: 11,
    color: "#374151",
    fontFamily: FontFamily.bold,
  },
  varianceRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  varianceRowLabel: {
    width: 108,
    fontSize: 12,
    color: "#111827",
    fontFamily: FontFamily.medium,
    textAlign: "right",
  },
  varianceTrackWrap: {
    flex: 1,
    paddingRight: 44,
  },
  varianceTrack: {
    height: 16,
    borderRadius: 3,
    backgroundColor: "#EAF1FB",
    position: "relative",
    overflow: "visible",
    justifyContent: "center",
  },
  varianceCenterLine: {
    position: "absolute",
    left: "50%",
    top: -10,
    bottom: -10,
    width: 3,
    backgroundColor: "#111827",
  },
  varianceCenterLineFirst: {
    top: 0,
  },
  varianceCenterLineLast: {
    bottom: 0,
  },
  varianceBarPositive: {
    position: "absolute",
    left: "50%",
    height: 16,
    borderTopRightRadius: 3,
    borderBottomRightRadius: 3,
  },
  varianceBarNegative: {
    position: "absolute",
    right: "50%",
    height: 16,
    backgroundColor: "#C12C01",
    borderTopLeftRadius: 3,
    borderBottomLeftRadius: 3,
  },
  varianceValueInline: {
    position: "absolute",
    top: -1,
    fontSize: 11,
    fontFamily: FontFamily.bold,
  },
  variancePositive: {
    color: "#005CBC",
  },
  varianceNegative: {
    color: "#C12C01",
  },
  varianceNegativeOnBar: {
    color: "#FFFFFF",
  },
  skeletonLoader: {
    backgroundColor: "#E5E7EB",
    borderRadius: 4,
    overflow: "hidden",
  },
  chartSkeletonPlot: {
    height: 230,
    borderRadius: 8,
    marginTop: 4,
  },
  chartCenterState: {
    minHeight: 180,
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  chartErrorText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: "#C12C01",
    textAlign: "center",
  },
  chartEmptyText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: "#6B7280",
    textAlign: "center",
  },
  retryButton: {
    marginTop: 4,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    backgroundColor: "#0B5FC6",
  },
  retryButtonText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: "#FFFFFF",
  },
});
